import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { publicApi } from "@/lib/api";
import { LchMark } from "@/components/public/BrandLogo";
import { Check, ArrowRight, Crown } from "lucide-react";
import { toast } from "sonner";

const SEEN_KEY = "lch-vip-popup-seen";
const DELAY_MS = 30000;

export default function VipPopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let seen = false;
    try {
      seen = window.localStorage.getItem(SEEN_KEY) === "1";
    } catch (e) {
      /* ignore */
    }
    if (seen) return;
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const markSeen = () => {
    try {
      window.localStorage.setItem(SEEN_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  };

  const close = () => {
    markSeen();
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setBusy(true);
    try {
      await publicApi.post("/public/vip-signup", {
        name: form.name,
        email: form.email,
        email_consent: true,
        preferred_contact: "email",
        favorite_brands: [],
        notes: "Joined via 30s welcome popup",
      });
      markSeen();
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        className="lch-public bg-[var(--panel)] border-[var(--border-2)] text-[var(--text)] sm:max-w-md p-0 overflow-hidden gap-0"
        data-testid="vip-popup"
      >
        {/* Gold header band */}
        <div className="relative px-8 pt-8 pb-6 text-center border-b border-[var(--border-lch)] bg-[var(--panel-2)]">
          <LchMark className="h-12 w-auto text-[#c9a86a] mx-auto" />
          <div className="text-[#c9a86a] tracking-[0.32em] text-[10px] mt-4">A PRIVATE INVITATION</div>
        </div>

        {done ? (
          <div className="px-8 py-10 text-center" data-testid="vip-popup-done">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full border border-[#c9a86a] text-[#c9a86a] mb-4">
              <Check size={26} />
            </div>
            <DialogTitle className="text-2xl font-serif text-[var(--text)]">You're on the ledger.</DialogTitle>
            <DialogDescription className="text-[var(--text-2)] mt-3 leading-relaxed">
              Welcome to the house. A member of the team will be in touch — keep an eye on your inbox.
            </DialogDescription>
            <Button
              onClick={close}
              data-testid="vip-popup-close-done"
              className="mt-6 bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none px-8"
            >
              Continue Browsing
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-8 py-8">
            <DialogTitle className="text-2xl md:text-[26px] font-serif text-center text-[var(--text)] leading-snug">
              Join the VIP List
            </DialogTitle>
            <DialogDescription className="text-[var(--text-2)] text-center mt-3 leading-relaxed">
              Get first calls on grails, private previews, and a standing seat at the table. Free to join.
            </DialogDescription>

            <div className="mt-6 space-y-3">
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Full name"
                aria-label="Full name"
                className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)] placeholder:text-[var(--muted)] h-11"
                data-testid="vip-popup-name"
              />
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email address"
                aria-label="Email address"
                className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)] placeholder:text-[var(--muted)] h-11"
                data-testid="vip-popup-email"
              />
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="vip-popup-submit"
              className="w-full mt-5 bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold tracking-wide rounded-none h-12 text-base group"
            >
              {busy ? "Joining…" : (<><Crown size={16} className="mr-2" /> Claim My Place <ArrowRight size={16} className="ml-2 transition-transform duration-200 group-hover:translate-x-0.5" /></>)}
            </Button>

            <button
              type="button"
              onClick={close}
              data-testid="vip-popup-dismiss"
              className="block w-full text-center mt-4 text-xs text-[var(--muted)] hover:text-[var(--text-2)] tracking-wide transition-colors"
            >
              Maybe later
            </button>
            <p className="text-[10px] text-[var(--muted)] text-center mt-3 tracking-wide">
              Military &amp; Veterans receive 10% off — verify securely at checkout.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
